<template>
  <RenderNode v-if="renderEmpty && (!items || items.length === 0)" :node="renderEmpty()" />
  <div
    v-else-if="!items || items.length === 0"
    :style="{ padding: '50px', textAlign: 'center', color: 'var(--p-surface-400)', fontSize: '14px' }"
  >
    No records match this search.
  </div>

  <!-- list / expanded → cards -->
  <div
    v-else-if="viewType === 'list' && density === 'expanded'"
    :style="{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '16px' }"
  >
    <template v-for="(r, i) in items" :key="r[idKey]">
      <RenderNode v-if="renderCard" :node="renderCard(cellArgs(r, i))" />
      <CollectionCard
        v-else
        :record="r"
        :config="config"
        :field-definitions="fieldDefinitions"
        :field-meta="fieldMeta"
        :cols="cols"
        :selected="selected(r)"
        @click="emit('open', r[idKey])"
        @dblclick="emit('open-expanded', r[idKey])"
      />
    </template>
  </div>

  <!-- list / compact → rows -->
  <div
    v-else-if="viewType === 'list'"
    :style="{ padding: compactPadded ? '16px' : 0, display: 'flex', flexDirection: 'column', gap: compactPadded ? '8px' : 0 }"
  >
    <template v-for="(r, i) in items" :key="r[idKey]">
      <RenderNode v-if="renderListRow" :node="renderListRow(cellArgs(r, i))" />
      <CollectionListRow
        v-else
        :record="r"
        :config="config"
        :field-definitions="fieldDefinitions"
        :field-meta="fieldMeta"
        :cols="cols"
        :selected="selected(r)"
        @click="emit('open', r[idKey])"
        @dblclick="emit('open-expanded', r[idKey])"
      />
    </template>
  </div>

  <!-- grid -->
  <div
    v-else-if="viewType === 'grid'"
    :style="{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${gridMinWidth}px, 1fr))`, gap: '16px', padding: '16px' }"
  >
    <template v-for="(r, i) in items" :key="r[idKey]">
      <RenderNode v-if="renderGridTile" :node="renderGridTile(cellArgs(r, i))" />
      <CollectionGridTile
        v-else
        :record="r"
        :config="config"
        :field-definitions="fieldDefinitions"
        :field-meta="fieldMeta"
        :cols="cols"
        :selected="selected(r)"
        @click="emit('open', r[idKey])"
        @dblclick="emit('open-expanded', r[idKey])"
      />
    </template>
  </div>

  <!-- table -->
  <CollectionTable
    v-else
    :results="items"
    :config="config"
    :field-definitions="fieldDefinitions"
    :field-meta="fieldMeta"
    :open-id="openId"
    :col-config="colConfig"
    @open="emit('open', $event)"
    @open-expanded="emit('open-expanded', $event)"
    @cycle-sort="emit('cycle-sort', $event)"
  />
</template>

<script setup>
// Pure list/card/grid/table presentation — no toolbar, no state. Ported from
// collection-frame CollectionBody. Emits open / open-expanded (record id) and
// cycle-sort (column key). Double-click → open-expanded is wired on every
// renderer so fullscreen-on-double-click works in list, grid, AND table.
import CollectionCard from './CollectionCard.vue'
import CollectionListRow from './CollectionListRow.vue'
import CollectionGridTile from './CollectionGridTile.vue'
import CollectionTable from './CollectionTable.vue'
import RenderNode from '@/admin-collection-layout/lib/RenderNode.js'

const props = defineProps({
  viewType: { type: String, default: 'list' },
  density: { type: String, default: 'expanded' },
  items: { type: Array, default: () => [] },
  idKey: { type: String, default: 'id' },
  config: { type: Object, default: () => ({}) },
  fieldDefinitions: { type: Object, required: true },
  fieldMeta: { type: Object, default: () => ({}) },
  cols: { type: Array, default: null },
  openId: { type: [String, Number], default: null },
  renderListRow: { type: Function, default: null },
  renderCard: { type: Function, default: null },
  renderGridTile: { type: Function, default: null },
  renderEmpty: { type: Function, default: null },
  gridMinWidth: { type: Number, default: 360 },
  compactPadded: { type: Boolean, default: false },
  colConfig: { type: Array, default: () => [] },
})
const emit = defineEmits(['open', 'open-expanded', 'cycle-sort'])

const selected = (r) => String(r[props.idKey]) === String(props.openId)
// cellArgs mirror the React rowProps + view context handed to custom renderers.
const cellArgs = (r, i) => ({
  record: r,
  config: props.config,
  fieldDefinitions: props.fieldDefinitions,
  fieldMeta: props.fieldMeta,
  cols: props.cols,
  selected: selected(r),
  onClick: () => emit('open', r[props.idKey]),
  onDoubleClick: () => emit('open-expanded', r[props.idKey]),
  viewType: props.viewType,
  density: props.density,
  index: i,
  openId: props.openId,
  idKey: props.idKey,
})
</script>
